import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ExpertInsights = ({ onReadMore }) => {
  const insights = [
  {
    id: 1,
    title: 'The Hidden Cost of Cheap Vacuums',
    excerpt: 'Why spending $50 more upfront can save you hundreds in the long run. Our 5-year cost analysis reveals surprising truths about vacuum ownership.',
    author: 'Dr. Sarah Mitchell',
    role: 'Home Appliance Engineer',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14e010f7e-1763293582323.png",
    avatarAlt: 'Professional headshot of Dr. Sarah Mitchell, a woman with shoulder-length brown hair wearing glasses and a navy blazer',
    readTime: '8 min read',
    category: 'Cost Analysis',
    publishDate: 'Oct 25, 2025',
    featured: true
  },
  {
    id: 2,
    title: 'HEPA Filters: Marketing Myth or Must-Have?',
    excerpt: 'We tested 47 vacuum filters in our lab to separate fact from fiction. The results will surprise you about what really matters for air quality.',
    author: 'Michael Rodriguez',
    role: 'Indoor Air Quality Specialist',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_143b978a3-1763294952544.png",
    avatarAlt: 'Professional headshot of Michael Rodriguez, a Hispanic man with short black hair in a navy suit',
    readTime: '12 min read',
    category: 'Health & Safety',
    publishDate: 'Oct 23, 2025',
    featured: false
  },
  {
    id: 3,
    title: 'Pet Hair Removal: Beyond the Marketing Claims',
    excerpt: 'Real-world testing with 12 different pet breeds reveals which vacuum features actually work and which are just clever marketing.',
    author: 'Jennifer Chen',
    role: 'Pet Care Consultant',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18a713e78-1763297858426.png",
    avatarAlt: 'Professional headshot of Jennifer Chen, an Asian woman with long black hair wearing a white blouse',
    readTime: '10 min read',
    category: 'Pet Owners',
    publishDate: 'Oct 21, 2025',
    featured: false
  }];


  const stats = [
  { label: 'Vacuums Tested', value: '247+', icon: 'TestTube' },
  { label: 'Hours of Testing', value: '1,200+', icon: 'Clock' },
  { label: 'Expert Reviews', value: '89', icon: 'Award' },
  { label: 'User Surveys', value: '15K+', icon: 'Users' }];


  return (
    <section className="py-16 bg-gradient-to-br from-muted/30 to-muted/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
            <Icon name="Microscope" size={16} className="mr-2" />
            Expert Research & Analysis
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Industry Insights & Deep Analysis
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Go beyond surface-level reviews with our in-depth research, laboratory testing, and industry insider knowledge that reveals what manufacturers don't want you to know.
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats?.map((stat, index) =>
          <div key={index} className="text-center">
              <div className="bg-white rounded-xl p-6 shadow-subtle">
                <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Icon name={stat?.icon} size={24} className="text-primary" />
                </div>
                <div className="text-2xl font-bold text-text-primary mb-1">{stat?.value}</div>
                <div className="text-sm text-text-secondary">{stat?.label}</div>
              </div>
            </div>
          )}
        </div>

        {/* Featured Insight */}
        <div className="mb-12">
          <div className="bg-white rounded-2xl shadow-floating overflow-hidden">
            <div className="lg:flex">
              <div className="lg:w-2/3 p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <span className="bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    Featured Analysis
                  </span>
                  <span className="text-sm text-text-secondary">{insights?.[0]?.category}</span>
                </div>
                
                <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
                  {insights?.[0]?.title}
                </h3>
                <p className="text-lg text-text-secondary mb-6 leading-relaxed">
                  {insights?.[0]?.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Image
                      src={insights?.[0]?.avatar}
                      alt={insights?.[0]?.avatarAlt}
                      className="w-10 h-10 rounded-full object-cover" />

                    <div>
                      <div className="font-medium text-text-primary">{insights?.[0]?.author}</div>
                      <div className="text-sm text-text-secondary">{insights?.[0]?.role}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    <span className="flex items-center">
                      <Icon name="Clock" size={14} className="mr-1" />
                      {insights?.[0]?.readTime}
                    </span>
                    <span>{insights?.[0]?.publishDate}</span>
                  </div>
                </div>
              </div>
              
              <div className="lg:w-1/3 bg-gradient-to-br from-primary to-secondary p-8 flex items-center justify-center">
                <div className="text-center text-white">
                  <Icon name="TrendingUp" size={64} className="mx-auto mb-4 opacity-80" />
                  <h4 className="text-xl font-semibold mb-2">Exclusive Research</h4>
                  <p className="text-white/90">
                    Based on 5 years of comprehensive testing and analysis
                  </p>
                </div>
              </div>
            </div>
            
            <div className="px-8 pb-8">
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => onReadMore(insights?.[0]?.id)}>

                Read Full Analysis
              </Button>
            </div>
          </div>
        </div>

        {/* Other Insights */}
        <div className="grid md:grid-cols-2 gap-8">
          {insights?.slice(1)?.map((insight) =>
          <div key={insight?.id} className="bg-white rounded-xl shadow-subtle hover:shadow-floating transition-all duration-300 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <span className="bg-muted text-text-secondary text-xs font-medium px-3 py-1 rounded-full">
                    {insight?.category}
                  </span>
                  <span className="text-sm text-text-secondary">{insight?.publishDate}</span>
                </div>
                
                <h3 className="text-xl font-semibold text-text-primary mb-3 hover:text-primary transition-colors cursor-pointer">
                  {insight?.title}
                </h3>
                <p className="text-text-secondary mb-6 leading-relaxed">
                  {insight?.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Image
                    src={insight?.avatar}
                    alt={insight?.avatarAlt}
                    className="w-8 h-8 rounded-full object-cover" />

                    <div>
                      <div className="text-sm font-medium text-text-primary">{insight?.author}</div>
                      <div className="text-xs text-text-secondary">{insight?.role}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-text-secondary flex items-center">
                      <Icon name="Clock" size={12} className="mr-1" />
                      {insight?.readTime}
                    </span>
                    <Button
                    variant="ghost"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => onReadMore(insight?.id)}
                    className="text-primary hover:text-primary/80">

                      Read
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default ExpertInsights;